// src/pages/AdminHome.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Card,
  Button,
  Avatar,
} from "@mui/material";

import GroupsIcon from "@mui/icons-material/Groups";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import SchoolIcon from "@mui/icons-material/School";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowDropUpRoundedIcon from "@mui/icons-material/ArrowDropUpRounded";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";

import AdminNavbar, { AdminTopBar } from "../components/AdminNavbar";
import TeacherView from "../components/TeacherView";
import GroupView from "../components/GroupView";
import NewTeacherForm from "../components/NewTeacherForm";
import NewGroupForm from "../components/NewGroupForm";

import { collection, getDocs, query, where } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { signOut } from "firebase/auth";
import { useAdminData } from "../context/AdminDataContext";
import { LEVELS, SUBJECTS } from "../constants";
import { useTranslation } from "react-i18next";

function AdminAllTeacher() {
  const { t } = useTranslation();
  const [teacherModalOpen, setTeacherModalOpen] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);

  const {
    students,
    setStudents,
    teachers,
    setTeachers,
    groups,
    setGroups,
    loading,
  } = useAdminData();

  // If current user is not admin => sign them out
  useEffect(() => {
    const verifyAdmin = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const adminQuery = query(
          collection(db, "admin"),
          where("uid", "==", user.uid)
        );
        const querySnapshot = await getDocs(adminQuery);

        if (querySnapshot.empty) {
          console.warn("User is not an admin. Signing out...");
          await signOut(auth);
        }
      } catch (err) {
        console.error("Error verifying admin access:", err);
        await signOut(auth);
      }
    };

    verifyAdmin();
  }, []);


  // ---------- DASHBOARD ANALYTICS (same style as Students page) ----------
  const totalTeachers = teachers.length;
  const totalGroups = groups.length;
  const totalStudents = students.length;

  const groupsWithTeacher = groups.filter((g) => !!g.teacherId).length;

  // Count students assigned to any group (based on student.enrollments)
  const assignedStudents = students.filter(
    (s) => Array.isArray(s.enrollments) && s.enrollments.length > 0
  ).length;

  const now = new Date();
  const last7Days = new Date();
  last7Days.setDate(now.getDate() - 7);

  const newTeachers7d = teachers.filter((t) => {
    if (!t.entryDate) return false;
    return new Date(t.entryDate) >= last7Days;
  }).length;

  const newGroups7d = groups.filter((g) => {
    if (!g.entryDate) return false;
    return new Date(g.entryDate) >= last7Days;
  }).length;

  return (
    <>
      {/* Top bar (logo left, bell right) */}
      <AdminTopBar />

      {/* Layout below the fixed AppBar */}
      <Box
        sx={{
          display: "flex",
          height: "calc(100vh - 64px)",
          mt: "64px",
          backgroundColor: "#ffffffff",
          flexDirection: "row-reverse",
          overflow: "hidden",
        }}
      >
        {/* RIGHT: Sidebar */}
        <AdminNavbar />

        {/* LEFT: Main content (scrollable) */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1, md: 2 },
            overflowY: "auto",
            direction: "rtl",
          }}
        >
          {/* Header card (Teachers & Groups) */}
          <Card
            elevation={0}
            sx={{
              mb: 2.5,
              borderRadius: 4,
              px: 3,
              py: 2.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            {/* RIGHT SIDE — TITLE + SUBTITLE + ICON */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {t("الأساتذة والأقسام")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t("إدارة جميع الأساتذة والأقسام داخل المنصة")}
                </Typography>
              </Box>
            </Box>

            {/* LEFT SIDE — BUTTONS */}
            <Box sx={{ display: "flex", gap: 1, direction: "ltr" }}>
              <Button variant="outlined" size="small">
                <ImportExportIcon /> {t("Export")}
              </Button>

              <Button
                variant="outlined"
                size="small"
                sx={{ borderRadius: 2 }}
                onClick={() => setGroupModalOpen(true)}
                startIcon={<GroupsIcon />}
              >
                {t("فتح قسم جديد")}
              </Button>

              <Button
                variant="contained"
                size="small"
                sx={{ borderRadius: 2 }}
                onClick={() => setTeacherModalOpen(true)}
                startIcon={<BusinessCenterIcon />}
              >
                {t("إضافة أستاذ")}
              </Button>
            </Box>
          </Card>

          {/* ===== DASHBOARD ANALYTICS (Teachers/Groups) — Shopify-like ===== */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: 2,
              mb: 3,
            }}
          >
            {/* helper "card" style */}
            {[
              {
                title: t("Total Teachers"),
                value: totalTeachers,
                deltaText: t("TBC% last week"),
                deltaUp: true,
                sparkFrom: "rgba(99,102,241,0.15)",
                sparkTo: "rgba(99,102,241,0.55)",
                icon: <BusinessCenterIcon sx={{ fontSize: 20, opacity: 0.9 }} />,
              },
              {
                title: t("Total Groups"),
                value: totalGroups,
                deltaText: t("TBC% last week"),
                deltaUp: true,
                sparkFrom: "rgba(14,165,233,0.15)",
                sparkTo: "rgba(14,165,233,0.55)",
                icon: <GroupsIcon sx={{ fontSize: 20, opacity: 0.9 }} />,
              },
              {
                title: t("Students Assigned"),
                value: assignedStudents,
                deltaText: t("TBC% last week"),
                deltaUp: false,
                sparkFrom: "rgba(234,88,12,0.15)",
                sparkTo: "rgba(234,88,12,0.55)",
                icon: <SchoolIcon sx={{ fontSize: 20, opacity: 0.9 }} />,
              },
              {
                title: t("New Teachers (7d)"),
                value: newTeachers7d,
                deltaText: t("{{count}} groups last week", { count: newGroups7d }),
                deltaUp: true,
                sparkFrom: "rgba(34,197,94,0.15)",
                sparkTo: "rgba(34,197,94,0.55)",
                icon: <TrendingUpIcon sx={{ fontSize: 20, opacity: 0.9 }} />,
              },
            ].map((item, idx) => (
              <Card
                key={idx}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid rgba(0,0,0,0.08)",
                  backgroundColor: "#fff",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                  px: 2.25,
                  py: 2,
                  position: "relative",
                  overflow: "hidden",
                  minHeight: 124,
                }}
              >
                {/* top row: label + small icon chip */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "rgba(0,0,0,0.55)",
                    }}
                  >
                    {item.title}
                  </Typography>

                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      backgroundColor: "rgba(0,0,0,0.04)",
                      border: "1px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    {item.icon}
                  </Box>
                </Box>

                {/* main number */}
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                  <Typography sx={{ fontSize: 40, fontWeight: 900, lineHeight: 1 }}>
                    {item.value}
                  </Typography>
                  <Typography sx={{ fontSize: 30, fontWeight: 900, color: "rgba(0,0,0,0.25)" }}>
                    -
                  </Typography>
                </Box>

                {/* bottom row: delta + sparkline */}
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: item.deltaUp ? "rgba(34,197,94,0.12)" : "rgba(148,163,184,0.18)",
                        color: item.deltaUp ? "rgba(22,163,74,0.95)" : "rgba(100,116,139,0.95)",
                      }}
                    >
                      {item.deltaUp ? (
                        <ArrowDropUpRoundedIcon sx={{ fontSize: 20 }} />
                      ) : (
                        <ArrowDropDownRoundedIcon sx={{ fontSize: 20 }} />
                      )}
                    </Box>

                    <Typography sx={{ fontSize: 13, color: "rgba(0,0,0,0.55)", fontWeight: 600 }}>
                      {item.deltaText}
                    </Typography>
                  </Box>

                  {/* fake sparkline (pure CSS, no chart lib) */}
                  <Box
                    sx={{
                      width: 96,
                      height: 26,
                      borderRadius: 2,
                      position: "relative",
                      overflow: "hidden",
                      backgroundColor: "rgba(0,0,0,0.03)",
                      border: "1px dashed rgba(0,0,0,0.10)",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background: `linear-gradient(90deg, transparent 0%, ${item.sparkFrom} 35%, ${item.sparkTo} 100%)`,
                        clipPath:
                          "polygon(0% 80%, 10% 72%, 20% 78%, 30% 58%, 40% 64%, 50% 46%, 60% 52%, 70% 34%, 80% 40%, 90% 22%, 100% 28%, 100% 100%, 0% 100%)",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 6,
                        height: 2,
                        opacity: 0.25,
                        backgroundColor: "rgba(0,0,0,0.25)",
                      }}
                    />
                  </Box>
                </Box>
              </Card>
            ))}
          </Box>

          {/* ===== CONTENT SECTIONS (same professional containers as Students page) ===== */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr" },
              gap: 2.5,
              mb: 2.5,
            }}
          >
            <TeacherView
              teachers={teachers}
              groups={groups}
              setTeachers={setTeachers}
              setGroups={setGroups}
              setStudents={setStudents}
            />
            {/* Groups view container */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: "#ffffff",
                boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
                border: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <Box>
                <GroupView
                  groups={groups}
                  teachers={teachers}
                  setGroups={setGroups}
                  students={students}
                  setStudents={setStudents}
                  setTeachers={setTeachers}
                />
              </Box>
            </Card>
          </Box>

          {/* Teacher modal */}
          <Dialog
            open={teacherModalOpen}
            onClose={() => setTeacherModalOpen(false)}
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 20px 60px rgba(15, 23, 42, 0.18)",
              },
            }}
          >
            <DialogTitle
              align="center"
              sx={{
                direction: "rtl",
                fontWeight: 800,
                fontSize: 22,
                py: 2,
                borderBottom: 1,
                borderColor: "divider",
                letterSpacing: "0.2px",
              }}
            >
              {t("إضافة أستاذ جديد")}
            </DialogTitle>
            <DialogContent
              sx={{
                direction: "rtl",
                p: { xs: 2, md: 3 },
                bgcolor: "background.default",
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
                "& .MuiInputBase-root": { borderRadius: 2 },
                "& .MuiButton-containedPrimary": {
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                },
                "& .MuiButton-outlinedPrimary": { borderRadius: 2 },
              }}
            >
              <NewTeacherForm
                onClose={() => setTeacherModalOpen(false)}
                setTeachers={setTeachers}
                teachers={teachers}
                levels={LEVELS}
                subjects={SUBJECTS}
              />
            </DialogContent>
          </Dialog>

          {/* Group modal */}
          <Dialog
            open={groupModalOpen}
            onClose={() => setGroupModalOpen(false)}
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 20px 60px rgba(15, 23, 42, 0.18)",
              },
            }}
          >
            <DialogTitle
              align="center"
              sx={{
                direction: "rtl",
                fontWeight: 800,
                fontSize: 22,
                py: 2,
                borderBottom: 1,
                borderColor: "divider",
                letterSpacing: "0.2px",
              }}
            >
              {t("إضافة مجموعة (قسم) جديدة")}
            </DialogTitle>
            <DialogContent
              sx={{
                direction: "rtl",
                p: { xs: 2, md: 3 },
                bgcolor: "background.default",
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
                "& .MuiInputBase-root": { borderRadius: 2 },
                "& .MuiButton-containedPrimary": {
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                },
                "& .MuiButton-outlinedPrimary": { borderRadius: 2 },
              }}
            >
              <NewGroupForm
                onClose={() => setGroupModalOpen(false)}
                setGroups={setGroups}
                teachers={teachers}
                levels={LEVELS}
                subjects={SUBJECTS}
              />
            </DialogContent>
          </Dialog>
        </Box>
      </Box>
    </>
  );
}

export default AdminAllTeacher;
